import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CartItem, Cart } from '../types/cart'

interface CartStore extends Cart {
  coupon_code: string | null
  discount: number
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void
  removeItem: (product_id: string) => void
  updateQuantity: (product_id: string, quantity: number) => void
  applyCoupon: (code: string, discount: number) => void
  removeCoupon: () => void
  clearCart: () => void
}

const calculateTotal = (items: CartItem[]): number => {
  return items.reduce((total, item) => total + item.price * item.quantity, 0)
}

const calculateItemCount = (items: CartItem[]): number => {
  return items.reduce((count, item) => count + item.quantity, 0)
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      total: 0,
      item_count: 0,
      coupon_code: null,
      discount: 0,

      addItem: (item, quantity = 1) => {
        const items = get().items
        const existingItem = items.find(i => i.product_id === item.product_id)

        let newItems: CartItem[]
        if (existingItem) {
          newItems = items.map(i =>
            i.product_id === item.product_id
              ? { ...i, quantity: i.quantity + quantity }
              : i
          )
        } else {
          newItems = [...items, { ...item, quantity }]
        }

        set({
          items: newItems,
          total: calculateTotal(newItems),
          item_count: calculateItemCount(newItems),
        })
      },

      removeItem: (product_id) => {
        const newItems = get().items.filter(i => i.product_id !== product_id)
        set({
          items: newItems,
          total: calculateTotal(newItems),
          item_count: calculateItemCount(newItems),
        })
      },

      updateQuantity: (product_id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(product_id)
          return
        }

        const newItems = get().items.map(i =>
          i.product_id === product_id ? { ...i, quantity } : i
        )
        set({
          items: newItems,
          total: calculateTotal(newItems),
          item_count: calculateItemCount(newItems),
        })
      },

      applyCoupon: (code, discount) => {
        set({
          coupon_code: code,
          discount: discount,
        })
      },

      removeCoupon: () => {
        set({
          coupon_code: null,
          discount: 0,
        })
      },

      clearCart: () => {
        set({
          items: [],
          total: 0,
          item_count: 0,
          coupon_code: null,
          discount: 0,
        })
      },
    }),
    {
      name: 'BotTola-cart',
      skipHydration: true,
      // Cart persists for 7 days
      version: 1,
      partialize: (state) => ({
        items: state.items,
        coupon_code: state.coupon_code,
        discount: state.discount,
        savedAt: new Date().getTime(),
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Check if cart is older than 7 days
          const savedAt = (state as any).savedAt || new Date().getTime()
          const daysSince = (new Date().getTime() - savedAt) / (1000 * 60 * 60 * 24)
          
          if (daysSince > 7) {
            // Clear expired cart
            state.items = []
            state.coupon_code = null
            state.discount = 0
            state.total = 0
            state.item_count = 0
          }
        }
      },
    }
  )
)

if (typeof window !== 'undefined') {
  const isFirstLoad = sessionStorage.getItem('BotTola-first-load') === null
  if (isFirstLoad) {
    useCartStore.getState().clearCart()
    sessionStorage.setItem('BotTola-first-load', 'false')
  }
}
